/**
 * Chapter audio on the device
 *
 * The app writes the cache; the service worker route only reads it. That split
 * matters: a self-populating cache-first route would happily store a partial or
 * unauthorised response and then serve it as a playable chapter.
 *
 * Audio is keyed by the same URL the player already uses, so `<audio src>` is
 * unchanged — the request is simply answered locally when the file is present.
 * See specs/013-offline-first-pwa/contracts/service-worker-routes.md.
 */

import { getApiUrl } from '../../config';
import { getAccessToken } from '../auth';
import * as repository from './repository';
import { nowIso } from './clockSkew';
import { reportReachable, reportUnreachable } from './connectivity';

export const ABSENT = 'absent';
export const QUEUED = 'queued';
export const DOWNLOADING = 'downloading';
export const READY = 'ready';
export const FAILED = 'failed';

/** The single place a chapter's audio URL is constructed. */
export const chapterAudioUrl = (projectId, chapterId) =>
  getApiUrl(`projects/${projectId}/chapters/${chapterId}.mp3`);

const listeners = new Set();

export const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

const announce = () => {
  for (const fn of listeners) {
    try {
      fn();
    } catch (e) {
      console.warn('audioCache: listener failed', e);
    }
  }
};

const openCache = async () => {
  const name = repository.audioCacheForAccount();
  if (!name) return null;
  // `caches` only exists in a secure context. Served over plain HTTP on
  // anything other than localhost, the Cache API is simply absent — which is a
  // configuration problem, not a download problem, and must say so.
  if (typeof caches === 'undefined') return null;
  return caches.open(name);
};

async function writeState(projectId, chapterId, patch) {
  const existing = (await repository.getDownload(chapterId)) || {};
  const row = {
    chapterId,
    projectId,
    state: ABSENT,
    bytes: 0,
    audioVersion: null,
    error: null,
    ...existing,
    ...patch,
    updatedAt: nowIso(),
  };
  await repository.putDownload(row);
  announce();
  return row;
}

export const getState = async (chapterId) => {
  try {
    const row = await repository.getDownload(chapterId);
    return row ? row.state : ABSENT;
  } catch (e) {
    return ABSENT;
  }
};

export const listDownloads = async () => {
  try {
    return await repository.getDownloads();
  } catch (e) {
    return [];
  }
};

const isQuotaError = (e) =>
  e && (e.name === 'QuotaExceededError' || e.code === 22 || /quota/i.test(e.message || ''));

/**
 * Download one chapter's audio for offline use.
 *
 * `ready` is written only after the cache write resolves. A row marked ready
 * before the bytes are actually stored is a Play control that fails offline —
 * the exact failure FR-023 exists to prevent.
 */
export const downloadChapter = async (projectId, chapter) => {
  const chapterId = chapter.chapter_id || chapter.id;
  if (!projectId || !chapterId) return null;

  if (navigator.onLine === false) {
    // Queued rather than failed: the `online` trigger picks it up (FR-020).
    return writeState(projectId, chapterId, { state: QUEUED, error: null });
  }

  await writeState(projectId, chapterId, { state: DOWNLOADING, error: null });
  const url = chapterAudioUrl(projectId, chapterId);

  try {
    const token = getAccessToken();
    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      console.warn(`audioCache: ${url} returned HTTP ${response.status}`);
      return writeState(projectId, chapterId, { state: FAILED, error: `HTTP ${response.status}` });
    }

    reportReachable();
    const bytes = Number(response.headers.get('content-length')) || 0;
    const cache = await openCache();
    if (!cache) {
      console.warn('audioCache: the Cache API is unavailable — this page needs a secure context (https, or localhost)');
      return writeState(projectId, chapterId, { state: FAILED, error: 'storage-unavailable' });
    }
    await cache.put(url, response);

    return writeState(projectId, chapterId, {
      state: READY,
      bytes,
      audioVersion: chapter.audio_version || null,
      error: null,
    });
  } catch (e) {
    // The reason has to reach the user, not just the row (FR-023). A bare
    // "TypeError: Failed to fetch" is nearly always CORS on a cross-origin API
    // — the audio element gets away with it because media requests are no-cors,
    // but fetch is not — so log the URL alongside it.
    console.warn(`audioCache: download failed for ${url}`, e);
    // Downloads use fetch, so they are outside the axios probe; a failure that
    // is not a storage problem is the server being unreachable.
    if (!isQuotaError(e)) reportUnreachable();
    // Out of space leaves everything already downloaded intact (FR-023).
    const error = isQuotaError(e) ? 'out-of-space' : 'network';
    return writeState(projectId, chapterId, { state: FAILED, error });
  }
};

/**
 * Download every chapter of a project that has audio on the server.
 *
 * Sequential on purpose: a dozen parallel multi-megabyte fetches compete for
 * the same connection and make every one of them slower, with no way to show
 * honest progress.
 *
 * @param {(done: number, total: number) => void} [onProgress]
 */
export const downloadProject = async (projectId, chapters, onProgress) => {
  const withAudio = (chapters || []).filter((c) => c.has_chapter_audio);
  let done = 0;
  if (onProgress) onProgress(0, withAudio.length);
  for (const chapter of withAudio) {
    await downloadChapter(projectId, chapter);
    done += 1;
    if (onProgress) onProgress(done, withAudio.length);
  }
  return { total: withAudio.length };
};

/** Remove one chapter's audio from the device. Safe to call when absent. */
export const evictChapter = async (projectId, chapterId) => {
  const cache = await openCache();
  if (cache) await cache.delete(chapterAudioUrl(projectId, chapterId));
  announce();
};

/**
 * Remove one chapter's download: the file AND its index row.
 *
 * `evictChapter` alone would leave a row still claiming `ready`, which is the
 * "Play control for audio that isn't there" failure FR-023 exists to prevent.
 */
export const removeChapterDownload = async (projectId, chapterId) => {
  await evictChapter(projectId, chapterId);
  try {
    await repository.deleteDownload(chapterId);
  } catch (e) {
    console.warn('audioCache: could not clear download row', e);
  }
  announce();
};

/** Remove a whole project's downloads, keeping its metadata browsable. */
export const removeProjectDownloads = async (projectId) => {
  const rows = (await listDownloads()).filter((r) => r.projectId === projectId);
  for (const row of rows) await removeChapterDownload(projectId, row.chapterId);
};

/**
 * Re-attempt everything left queued or failed. Called on the `online` trigger
 * so an interrupted download resumes without the user asking again (FR-020).
 */
export const retryPending = async (chaptersByProject = {}) => {
  const rows = (await listDownloads()).filter((r) => r.state === QUEUED || r.state === FAILED);
  for (const row of rows) {
    const chapters = chaptersByProject[row.projectId] || [];
    const chapter = chapters.find((c) => (c.chapter_id || c.id) === row.chapterId);
    await downloadChapter(row.projectId, chapter || { chapter_id: row.chapterId });
  }
};

/**
 * Reconcile the download index against what the cache actually holds, and
 * against the server's current audio version.
 *
 * Two failure modes, both silent without this: the browser evicts stored audio
 * (FR-024), and the server replaces a chapter's narration so the copy here is
 * superseded (FR-033). Version comparison is dormant until the API exposes
 * `audio_version` — an absent version is not treated as a change.
 */
export const reconcile = async (projectId, chapters = []) => {
  const cache = await openCache();
  const rows = (await listDownloads()).filter((r) => r.projectId === projectId);
  const byId = new Map(chapters.map((c) => [c.chapter_id || c.id, c]));

  for (const row of rows) {
    if (row.state !== READY) continue;

    const present = cache ? await cache.match(chapterAudioUrl(projectId, row.chapterId)) : null;
    if (!present) {
      await writeState(projectId, row.chapterId, { state: ABSENT, bytes: 0, error: null });
      continue;
    }

    const serverVersion = byId.get(row.chapterId)?.audio_version;
    if (serverVersion && row.audioVersion && serverVersion !== row.audioVersion) {
      await evictChapter(projectId, row.chapterId);
      await writeState(projectId, row.chapterId, { state: QUEUED, bytes: 0, error: null });
    }
  }
  announce();
};
